import React from 'react';
import { DocumentLineItemWorkOrder } from '../types/Types';
import { parseDecimalInput, formatMoney } from '../utils/decimalInput';
import { createIssuedRows, IssueSerialSelection } from './useIssueSerials';

interface UseNestedDocumentQuantityModalParams {
  documentId: string;
  localRows: DocumentLineItemWorkOrder[];
  setLocalRows: React.Dispatch<React.SetStateAction<DocumentLineItemWorkOrder[]>>;
  editDocumentLineItemWorkOrder: (id: string, payload: any) => Promise<any>;
  createDocumentLineItemWorkOrder: (payload: any) => Promise<any>;
  refetch: () => Promise<void> | void;
  setSavingRows: React.Dispatch<React.SetStateAction<Set<number>>>;
}

/** Handles serial selection and saves each issued serial as a separate nested row. */
export function useNestedDocumentQuantityModal({
  documentId,
  localRows,
  setLocalRows,
  editDocumentLineItemWorkOrder,
  createDocumentLineItemWorkOrder,
  refetch,
  setSavingRows,
}: UseNestedDocumentQuantityModalParams) {
  const [showQuantityModal, setShowQuantityModal] = React.useState(false);
  const [selectedRowForQuantity, setSelectedRowForQuantity] = React.useState<DocumentLineItemWorkOrder | null>(null);
  const [selectedPartnerId, setSelectedPartnerId] = React.useState<string | undefined>();

  const parseNumber = (value?: string | number): number => parseDecimalInput(value) ?? 0;

  const handleQuantityClick = (rowIndex: number) => {
    const row = localRows[rowIndex];
    if (!row?.item?._id) return;
    const warehouseId = typeof row.warehouse === 'object' ? row.warehouse?._id : row.warehouse;
    setSelectedRowForQuantity(row);
    setSelectedPartnerId(warehouseId || undefined);
    setShowQuantityModal(true);
  };

  const handleSaveSerialChanges = async (serials: IssueSerialSelection[]) => {
    if (!selectedRowForQuantity || serials.length === 0) return;
    const rowIndex = localRows.findIndex((row) => row === selectedRowForQuantity);
    if (rowIndex === -1) return;

    const original = localRows[rowIndex];
    const replacements = createIssuedRows(original, serials, (row, quantity) => ({
      ...row,
      quantity,
      priceValue: formatMoney(parseNumber(row.purchasePrice) * parseNumber(quantity)),
    }));
    const warehouseId = typeof original.warehouse === 'object'
      ? original.warehouse?._id
      : original.warehouse;

    setSavingRows((previous) => new Set(previous).add(rowIndex));
    try {
      for (const [index, row] of replacements.entries()) {
        const payload = {
          document: documentId,
          item: row.item?._id,
          warehouse: warehouseId,
          purchasePrice: parseNumber(row.purchasePrice),
          valuationPrice: parseNumber(row.valuationPrice),
          quantity: parseNumber(row.quantity),
          quantityOS: parseNumber(row.quantityOS),
          quantityWorkOrder: parseNumber(row.quantityWorkOrder),
          serial: row.serial,
          deadline: row.deadline,
          priceValue: parseNumber(row.priceValue),
        };
        if (index === 0 && original._id) {
          await editDocumentLineItemWorkOrder(original._id, payload);
        } else {
          await createDocumentLineItemWorkOrder(payload);
        }
      }
      setLocalRows((current) => [
        ...current.slice(0, rowIndex),
        ...replacements.map((row, index) => index === 0 ? row : { ...row, _id: undefined }),
        ...current.slice(rowIndex + 1),
      ]);
      await refetch();
    } catch (error) {
      await refetch();
      throw error;
    } finally {
      setSavingRows((previous) => {
        const next = new Set(previous);
        next.delete(rowIndex);
        return next;
      });
    }
  };

  return {
    showQuantityModal,
    setShowQuantityModal,
    selectedRowForQuantity,
    setSelectedRowForQuantity,
    selectedPartnerId,
    handleQuantityClick,
    handleSaveSerialChanges,
  };
}

export default useNestedDocumentQuantityModal;
